import React, { useState, useEffect } from "react";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import Dropdown from "../../../components/Dropdown";
import Checkbox from "../../../components/Checkbox";
import Dialog from "../../../components/Dialog";
import { ALPHA_NUMERIC_SPACE, testRegex } from "../../../config/regex";

const TYPE_OPTIONS = [
  { value: "Full-time", label: "Full-time" },
  { value: "Part-time", label: "Part-time" },
  { value: "Contract", label: "Contract" },
  { value: "Internship", label: "Internship" },
  { value: "Remote", label: "Remote" },
];

const DEPT_OPTIONS = [
  { value: "Engineering", label: "Engineering" },
  { value: "Marketing", label: "Marketing" },
  { value: "Sales", label: "Sales" },
  { value: "Content", label: "Content" },
  { value: "Finance", label: "Finance" },
  { value: "HR", label: "HR" },
];

const EXPERIENCE_OPTIONS = [
  { value: "Fresher", label: "Fresher" },
  { value: "1-2 Years", label: "1-2 Years" },
  { value: "3-5 Years", label: "3-5 Years" },
  { value: "5-10 Years", label: "5-10 Years" },
  { value: "10+ Years", label: "10+ Years" },
];

const SALARY_OPTIONS = [
  { value: "₹3L - ₹5L", label: "₹3L - ₹5L" },
  { value: "₹5L - ₹10L", label: "₹5L - ₹10L" },
  { value: "₹10L - ₹15L", label: "₹10L - ₹15L" },
  { value: "₹15L - ₹25L", label: "₹15L - ₹25L" },
  { value: "₹25L+", label: "₹25L+" },
  { value: "Disclosed on interview", label: "Disclosed on interview" },
];

const SKILL_OPTIONS = [
  { value: "React, Node.js, MongoDB", label: "Full Stack" },
  { value: "Excel, Python, SQL", label: "Data Analyst" },
  { value: "Trading, Technical Analysis", label: "Trader" },
  { value: "SEO, SEM, Social Media", label: "Marketing" },
];

const AddJobOpening = ({ open, onClose, onSuccess, editJob }) => {
  const [form, setForm] = useState({
    title: "",
    department: "",
    location: "",
    type: "",
    experience: "",
    salary: "",
    skills: "",
    description: "",
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(editJob?.id);

  useEffect(() => {
    if (!open) return;
    if (editJob) {
      setForm({
        title: editJob.title || "",
        department: editJob.department || "",
        location: editJob.location || "",
        type: editJob.type || "",
        experience: editJob.experience || "",
        salary: editJob.salary || "",
        skills: Array.isArray(editJob.skills)
          ? editJob.skills.join(", ")
          : editJob.skills || "",
        description: editJob.description || "",
        isActive: editJob.isActive !== false,
      });
    } else {
      setForm({
        title: "",
        department: "",
        location: "",
        type: "",
        experience: "",
        salary: "",
        skills: "",
        description: "",
        isActive: true,
      });
    }
    setErrors({});
    setTouched({});
  }, [open, editJob]);

  const validateField = (name, value) => {
    const v = String(value || "").trim();
    switch (name) {
      case "title":
        if (!v) return "Job title is required";
        if (!testRegex(ALPHA_NUMERIC_SPACE, v)) return "Title contains invalid characters";
        if (v.length < 3) return "Title is too short";
        return "";
      case "location":
        if (!v) return "Location is required";
        if (!testRegex(ALPHA_NUMERIC_SPACE, v)) return "Location contains invalid characters";
        return "";
      case "department":
        if (!v) return "Department is required";
        return "";
      case "type":
        if (!v) return "Job type is required";
        return "";
      case "experience":
        if (!v) return "Experience is required";
        return "";
      case "salary":
        if (!v) return "Salary range is required";
        return "";
      case "skills":
        if (!v) return "Primary skills are required";
        return "";
      case "description":
        if (!v) return "Description is required";
        if (v.length < 10) return "Description should be at least 10 characters";
        return "";
      default:
        return "";
    }
  };

  const isFormInvalid = React.useMemo(() => {
    const requiredFields = ["title", "department", "location", "type", "experience", "salary", "skills", "description"];
    const hasEmptyRequired = requiredFields.some(f => !String(form[f] || "").trim());
    const hasErrors = Object.values(errors).some(msg => msg !== "");
    return hasEmptyRequired || hasErrors;
  }, [form, errors]);

  const hasChanged = React.useMemo(() => {
    if (!isEdit || !editJob) return true;
    const initialSkills = Array.isArray(editJob.skills)
      ? editJob.skills.join(", ")
      : editJob.skills || "";
    
    return (
      form.title !== (editJob.title || "") ||
      form.department !== (editJob.department || "") ||
      form.location !== (editJob.location || "") ||
      form.type !== (editJob.type || "") ||
      form.experience !== (editJob.experience || "") ||
      form.salary !== (editJob.salary || "") ||
      form.skills !== initialSkills ||
      form.description !== (editJob.description || "") ||
      form.isActive !== (editJob.isActive !== false)
    );
  }, [form, editJob, isEdit]);

  const handleChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSave = async () => {
    const requiredFields = ["title", "department", "location", "type", "experience", "salary", "skills", "description"];
    const newErrors = {};
    requiredFields.forEach(f => {
      const err = validateField(f, form[f]);
      if (err) newErrors[f] = err;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(requiredFields.reduce((acc, f) => ({ ...acc, [f]: true }), {}));
      return;
    }

    setSaving(true);
    const payload = {
      title: form.title.trim(),
      department: form.department.trim(),
      location: form.location.trim(),
      type: form.type || "Full-time",
      experience: form.experience.trim(),
      salary: form.salary.trim(),
      skills: form.skills.trim()
        ? form.skills
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
      description: form.description.trim(),
      isActive: form.isActive,
    };

    try {
      await onSuccess?.(payload, isEdit ? editJob.id : null);
      setErrors({});
      // Modal is closed by parent on success
    } catch (err) {
      // Error handled by parent/Swal
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" size="sm" onClick={onClose}>
        Cancel
      </Button>
      <Button 
        variant="primary" 
        size="sm" 
        onClick={handleSave}
        disabled={isFormInvalid || saving || (isEdit && !hasChanged)}
      >
        {saving ? "Saving..." : (isEdit ? "Update" : "Add Job")}
      </Button>
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Job Opening" : "Add Job Opening"}
      size="xl"
      footer={footer}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextInput
          name="title"
          label="Job title"
          placeholder="e.g. Senior Financial Analyst"
          value={form.title}
          onChange={(e) => handleChange("title", e.target.value)}
          error={touched.title ? errors.title : ""}
          required
        />
        <Dropdown
          name="department"
          label="Department"
          value={form.department}
          onChange={(e) => handleChange("department", e.target.value)}
          options={DEPT_OPTIONS}
          error={touched.department ? errors.department : ""}
          required
        />
        <TextInput
          name="location"
          label="Location"
          placeholder="e.g. Hyderabad, India / Remote"
          value={form.location}
          onChange={(e) => handleChange("location", e.target.value)}
          error={touched.location ? errors.location : ""}
          required
        />
        <Dropdown
          name="type"
          label="Job type"
          value={form.type}
          onChange={(e) => handleChange("type", e.target.value)}
          options={TYPE_OPTIONS}
          placeholder="Type"
          error={touched.type ? errors.type : ""}
          required
        />
        <Dropdown
          name="experience"
          label="Experience"
          value={form.experience}
          onChange={(e) => handleChange("experience", e.target.value)}
          options={EXPERIENCE_OPTIONS}
          error={touched.experience ? errors.experience : ""}
          required
        />
        <Dropdown
          name="salary"
          label="Salary range"
          value={form.salary}
          onChange={(e) => handleChange("salary", e.target.value)}
          options={SALARY_OPTIONS}
          error={touched.salary ? errors.salary : ""}
          required
        />
      </div>
      <div className="mt-4">
        <Dropdown
          name="skills"
          label="Primary Skills"
          value={form.skills}
          onChange={(e) => handleChange("skills", e.target.value)}
          options={SKILL_OPTIONS}
          error={touched.skills ? errors.skills : ""}
          required
        />
      </div>
      <div className="mt-4">
        <TextInput
          name="description"
          label="Description"
          placeholder="Short description of the role"
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
          multiline
          rows={3}
          error={touched.description ? errors.description : ""}
          required
        />
      </div>
      {isEdit && (
        <div className="mt-2">
          <Checkbox
            name="isActive"
            label="Visible on Career page"
            checked={form.isActive}
            onChange={(e) => handleChange("isActive", e.target.checked)}
          />
        </div>
      )}
    </Dialog>
  );
};

export default AddJobOpening;
